// Champion prefabs (legends.enabled) — docs/champion-system-design.md D12.
//
// `legend_dragon` is the player-owned Dragon Champion. It re-skins the
// hostile "boss" Dragon from creatures.ts for ownership: same silhouette,
// but not a boss/neutral faction, with team-coloured accent voxels (wing-tip
// trim, dorsal crest, tail band) so the owner's colour reads from far away.
// Greys are mapped onto the owner's colour by the renderer (BlockVoxel.team).
// The render scale (x1.6) and hover live in apps/app render/unitRenderStyle.ts,
// never in the voxel data (the board can be scaled; see boardScale.ts).
//
// Phase B adds `legend_golem` (a hulking stone guardian, authored here) and
// `legend_lich` (the hostile Lich re-skinned with team trim, glowing eyes/orb
// and orbiting soul wisps). Unit kinds `golem` / `lich` map to them in
// registry.ts; scale, hover and bob live in unitRenderStyle.ts.

import { creaturePrefabs } from './creatures.js';
import { TEAM_DARK, TEAM_LIGHT, box } from './creatures_extra_shared.js';
import type { CharacterPart, CharacterPrefab } from './types.js';

const base = creaturePrefabs.dragon as CharacterPrefab;

const TEAM_PARTS: readonly CharacterPart[] = [
  // Trim along the top edge of the outer wing membranes.
  {
    name: 'team_wing_trim_left',
    voxels: box(-12, 14, 0, 4, 1, 6, TEAM_LIGHT, { team: true }),
  },
  {
    name: 'team_wing_trim_right',
    voxels: box(8, 14, 0, 4, 1, 6, TEAM_LIGHT, { team: true }),
  },
  // Dorsal crest riding the existing spine row.
  {
    name: 'team_crest',
    voxels: [
      ...box(0, 15, -4, 1, 1, 1, TEAM_LIGHT, { team: true }),
      ...box(0, 14, -5, 1, 1, 1, TEAM_LIGHT, { team: true }),
      ...box(0, 13, -6, 1, 1, 1, TEAM_DARK, { team: true }),
    ],
  },
  // Band across the tail root.
  { name: 'team_tail_band', voxels: box(-2, 12, -8, 4, 1, 2, TEAM_DARK, { team: true }) },
];

const legendDragon: CharacterPrefab = {
  name: 'Dragon Champion',
  description: 'Player-owned Dragon: the hostile boss Dragon re-skinned with team accents',
  maxHp: base.maxHp,
  canFly: true,
  skeletonType: 'quadruped',
  // Owned by a player, not a neutral boss: no `faction: 'boss'` / `isBoss`.
  faction: 'champion',
  parts: [...base.parts, ...TEAM_PARTS],
};

// ─── Golem ───────────────────────────────────────────────────
const STONE = '#7a766f';
const STONE_DARK = '#6e6a64';
const STONE_PLATE = '#8a857d';
const RUNE = '#ffb347';
const RUNE_GLOW = '#ff8a1f';

const legendGolem: CharacterPrefab = {
  name: 'Golem Champion',
  description: 'Player-owned stone Golem: broad, slow, runed, with team shoulder pads and belt',
  maxHp: 90,
  faction: 'champion',
  skeletonType: 'humanoid',
  parts: [
    {
      name: 'head',
      voxels: [
        ...box(-3, 18, -3, 6, 5, 6, STONE_DARK),
        // Glowing rune eyes and a brow ridge.
        { x: -2, y: 21, z: 3, color: RUNE, emissive: RUNE_GLOW },
        { x: 1, y: 21, z: 3, color: RUNE, emissive: RUNE_GLOW },
        ...box(-3, 22, 3, 6, 1, 1, STONE_PLATE),
      ],
    },
    {
      name: 'body',
      voxels: [
        ...box(-6, 8, -3, 12, 10, 7, STONE),
        ...box(-4, 10, 4, 8, 6, 1, STONE_PLATE),
        // The rune core in the chest.
        ...box(-1, 12, 4, 2, 2, 1, RUNE, { emissive: RUNE_GLOW }),
        // Cracks.
        { x: -4, y: 16, z: 4, color: '#3d3a36' },
        { x: -3, y: 15, z: 4, color: '#3d3a36' },
        { x: 3, y: 11, z: 4, color: '#3d3a36' },
        // Moss.
        { x: 2, y: 17, z: 4, color: '#4a5a3a' },
        { x: -5, y: 9, z: 4, color: '#4a5a3a' },
      ],
    },
    {
      name: 'team_trim',
      voxels: [
        // Shoulder pads, belt and a banner down the back carry the owner's colour.
        ...box(-9, 17, -2, 3, 2, 5, TEAM_LIGHT, { team: true }),
        ...box(6, 17, -2, 3, 2, 5, TEAM_LIGHT, { team: true }),
        ...box(-6, 8, 4, 12, 1, 1, TEAM_DARK, { team: true }),
        ...box(-2, 9, -4, 4, 8, 1, TEAM_LIGHT, { team: true }),
      ],
    },
    {
      name: 'left_arm',
      voxels: [...box(-10, 8, -2, 4, 11, 5, STONE), ...box(-11, 4, -3, 6, 4, 7, STONE_DARK)],
    },
    {
      name: 'right_arm',
      voxels: [...box(6, 8, -2, 4, 11, 5, STONE), ...box(5, 4, -3, 6, 4, 7, STONE_DARK)],
    },
    {
      name: 'left_leg',
      voxels: [...box(-5, 2, -2, 4, 6, 5, STONE), ...box(-6, 0, -3, 5, 2, 7, STONE_DARK)],
    },
    {
      name: 'right_leg',
      voxels: [...box(1, 2, -2, 4, 6, 5, STONE), ...box(1, 0, -3, 5, 2, 7, STONE_DARK)],
    },
  ],
};

// ─── Lich ────────────────────────────────────────────────────
const baseLich = creaturePrefabs.lich as CharacterPrefab;
const EYE_RED = '#8b0000';
const ORB_GREEN = '#22c55e';

/** The hostile Lich's voxels with the red eyes and staff orb made emissive. */
const glowing = (part: CharacterPart): CharacterPart => ({
  ...part,
  voxels: part.voxels.map((v) =>
    v.color === EYE_RED
      ? { ...v, emissive: '#ff2a2a' }
      : v.color === ORB_GREEN
        ? { ...v, emissive: '#22ff88' }
        : v,
  ),
});

const LICH_TEAM_PARTS: readonly CharacterPart[] = [
  // Sash across the chest and a hem trim on the cape.
  { name: 'team_sash', voxels: box(-3, 10, 2, 6, 1, 1, TEAM_LIGHT, { team: true }) },
  { name: 'team_hem', voxels: box(-3, 7, -3, 6, 1, 1, TEAM_DARK, { team: true }) },
  // Soul wisps orbiting the Lich.
  {
    name: 'soul_wisps',
    voxels: [
      { x: -8, y: 14, z: 1, color: '#9df5b8', emissive: '#7cfc9a' },
      { x: 8, y: 17, z: -1, color: '#9df5b8', emissive: '#7cfc9a' },
      { x: -7, y: 20, z: -3, color: '#9df5b8', emissive: '#7cfc9a' },
      { x: 7, y: 11, z: 2, color: '#9df5b8', emissive: '#7cfc9a' },
    ],
  },
];

const legendLich: CharacterPrefab = {
  name: 'Lich Champion',
  description: 'Player-owned Lich: the hostile Lich with team trim, glowing eyes and soul wisps',
  maxHp: baseLich.maxHp,
  faction: 'champion',
  skeletonType: 'humanoid',
  parts: [...baseLich.parts.map(glowing), ...LICH_TEAM_PARTS],
};

export const LEGEND_PREFABS: Readonly<Record<string, CharacterPrefab>> = {
  legend_dragon: legendDragon,
  legend_golem: legendGolem,
  legend_lich: legendLich,
};
